import { CommonModule, NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, Inject, Input, OnInit, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAnchor, MatButton } from '@angular/material/button';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatIcon } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  AppType,
  Charity,
  ENVIRONMENT,
  Environment,
  LandingConfiguration,
  Screening,
  SplitScreen,
  SplitScreenSectionsTypes,
  SplitScreenVersion,
  WindowSize,
} from '@domains';
import { select, Store } from '@ngrx/store';
import { LandingConfigurationService } from '@rspl-api';
import {
  CardComponent,
  FxFlexDirective,
  FxLayoutAlignDirective,
  FxLayoutDirective,
  FxLayoutGapDirective,
  Responsive,
  ResponsiveService,
  SafePipe,
} from '@rspl-ui';
import { ColorPickerModule } from 'ngx-color-picker';
import { take, takeUntil } from 'rxjs/operators';

import { getCharityScreening } from '../../store/charity/charity.actions';
import { selectCharityScreening } from '../../store/charity/charity.selectors';
import { SplitScreenFormComponent } from './split-screen-form/split-screen-form.component';

@Component({
  selector: 'app-split-screen',
  templateUrl: './split-screen.component.html',
  styleUrls: ['./split-screen.component.scss'],
  imports: [
    FxLayoutDirective,
    FxFlexDirective,
    MatIcon,
    FxLayoutGapDirective,
    FxLayoutAlignDirective,
    MatButton,
    NgClass,
    MatAnchor,
    NgTemplateOutlet,
    FormsModule,
    ReactiveFormsModule,
    CardComponent,
    MatButtonToggleGroup,
    MatButtonToggle,
    ColorPickerModule,
    SafePipe,
    CommonModule,
    SplitScreenFormComponent,
  ],
})
export class SplitScreenComponent extends Responsive implements OnInit {
  @ViewChild(SplitScreenFormComponent) formComponent!: SplitScreenFormComponent;
  @Input() set charity(charity: Charity) {
    this.#charity = charity;
    if (this.charity) {
      this.getScreening();
      this.setData();
    }
  }

  #charity: Charity;
  splitScreenVersions = SplitScreenVersion;
  splitscreensectionTypes = SplitScreenSectionsTypes;
  app: AppType;
  versions = [
    { value: SplitScreenVersion.V1, text: 'Design v1' },
    { value: SplitScreenVersion.V2, text: 'Design v2' },
    { value: SplitScreenVersion.V3A, text: 'Design v3A' },
    { value: SplitScreenVersion.V3B, text: 'Design v3B' },
    { value: SplitScreenVersion.V4, text: 'Design v4' },
  ];
  landingConfiguration: LandingConfiguration;
  formData: SplitScreen;
  preview: 'mobile' | 'desktop' = 'desktop';
  screening?: Screening;
  fullscreen = false;
  isCaptain: boolean;
  private iframeOrigin: string;

  get charity(): Charity {
    return this.#charity;
  }

  get linkToSplitLanding() {
    return `${this.environment.urls.donor}/start/charity/${this.charity.id}?isPreview=true`;
  }

  get textValues(): string[] {
    return this.formData.text?.trim().length > 0 ? this.config.text?.trim().split('\n') || [] : [];
  }

  get textValue(): string {
    return this.formData.text?.trim().length > 0 ? this.config.text?.trim().replaceAll('\n', '<br/>') : undefined;
  }

  get textPrimaryValues(): string[] {
    return this.formData.textPrimary?.split('\n') || [];
  }

  get textPrimaryValue(): string {
    return this.formData.textPrimary?.replaceAll('\n', '<br/>') || undefined;
  }

  get isDesktop(): boolean {
    return this.preview === 'desktop';
  }

  get pickUp(): boolean {
    return this.charity.meta.pickUp;
  }

  get iframeStyle() {
    const scale = this.fullscreen || !this.isDesktop ? 1 : 0.5;

    return {
      transform: `scale(${scale})`,
      'transform-origin': 'top left',
      height: `${(1 / scale) * 100}%`,
      width: `${(1 / scale) * 100}%`,
    };
  }

  constructor(
    private store: Store,
    public override responsiveService: ResponsiveService,
    private snackBar: MatSnackBar,
    private landingConfigurationService: LandingConfigurationService,
    @Inject(ENVIRONMENT) private environment: Environment,
  ) {
    super(responsiveService);
    this.isCaptain = environment.app === AppType.CAPTAIN;
    this.app = this.environment.app;
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.responsiveService.size.pipe(takeUntil(this.destroy$)).subscribe((size) => {
      if ([WindowSize.XS, WindowSize.SM].includes(size)) {
        this.preview = 'mobile';
      }
    });

    this.store.pipe(select(selectCharityScreening), takeUntil(this.destroy$)).subscribe((screening) => {
      this.screening = screening;
    });

    window.addEventListener('message', this.onMessage.bind(this));
  }

  onMessage(event: MessageEvent) {
    if (event.data?.previewReady) {
      this.iframeOrigin = event.origin;
    }
    const targetOrigin = this.iframeOrigin || new URL(this.linkToSplitLanding).origin;
    const iframe = window.document.querySelector('iframe');
    iframe?.contentWindow?.postMessage({ splitscreenConfig: this.formData, charityLogo: this.charity?.logo }, targetOrigin);
  }

  private setData(): void {
    this.landingConfigurationService
      .filter({ resource_type: 'Charity', resource_id: this.charity.id })
      .pipe(take(1))
      .subscribe((configs) => {
        if (configs.results?.length > 0) {
          this.landingConfiguration = configs.results[0];
        } else {
          this.landingConfiguration = new LandingConfiguration({
            resource_type: 'Charity',
            resource_id: this.charity.id,
          });
        }
        this.formData = this.config;
      });
  }

  private getScreening() {
    this.store.dispatch(getCharityScreening({ charityId: this.charity.id }));
  }

  formDataChange(data: SplitScreen) {
    const iframe = window.document.querySelector('iframe');
    const targetOrigin = this.iframeOrigin || new URL(this.linkToSplitLanding).origin;
    iframe?.contentWindow?.postMessage({ splitscreenConfig: data, charityLogo: this.charity?.logo }, targetOrigin);

    this.formData = data;
  }

  submitForm(): void {
    if (this.formComponent.validateForm()) {
      (this.landingConfiguration?.id
        ? this.landingConfigurationService.update(
            this.landingConfiguration.id,
            new LandingConfiguration({
              ...this.landingConfiguration,
              configuration: this.formData,
            }),
          )
        : this.landingConfigurationService.create(
            new LandingConfiguration({
              ...this.landingConfiguration,
              configuration: this.formData,
            }),
          )
      )
        .pipe(take(1))
        .subscribe((config) => {
          this.snackBar.open('Split Landing Page configuration updated successfully!', null, {
            duration: 5000,
            panelClass: 'success',
          });
          this.landingConfiguration = config;
          this.formData = this.config;
        });
    } else {
      this.snackBar.open(
        [SplitScreenVersion.V1, SplitScreenVersion.V2].includes(this.config.version)
          ? 'There are some validation errors! Please check all Split Landing Page configuration tabs and try again!'
          : 'There are some validation errors!',
        null,
        {
          duration: 5000,
          panelClass: 'error',
        },
      );
    }
  }

  toggleFullScreen(): void {
    this.fullscreen = !this.fullscreen;
    this.fullscreen ? document.body.classList.add('fullscreen') : document.body.classList.remove('fullscreen');
  }

  setPreview(preview: 'mobile' | 'desktop'): void {
    this.preview = preview;
  }

  get config(): SplitScreen {
    return this.landingConfiguration?.configuration;
  }
}
